import {
	Dispatch,
	FunctionComponent,
	SetStateAction,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useTrackFeature } from "@/utils/productAnalytics/useProductAnalytics";

import { useSnackBar } from "@/utils/useSnackbar";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { contactSelector, openContactLeadPanel } from "@/pages/Private/redux/contact/contact.slice";
import {
	clearGlobalSearch,
	clearSelectedCompany,
	searchSelector,
} from "@/pages/Private/redux/search/search.slice";
import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";
import { ContactLeadPanel } from "@/pages/Private/pages/Contacts/components/ContactLeadPanel";
import { toContactProspectPreview } from "@/pages/Private/pages/Contacts/utils/toContactProspectPreview";
import { resolveChatListPersonId } from "@/pages/Private/pages/Contacts/utils/resolveChatListPersonId";
import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";
import { useCampaignStatusPolling } from "@/pages/Private/pages/List/hooks/useCampaignStatusPolling";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import { Modal } from "../Modals/Modal";
import { ByNameTab } from "./ByNameTab";
import { ByFiltersTab } from "./ByFiltersTab";
import { hasDisplayableCompany } from "./utils";
import { ByUrlTab, ByUrlFooterStatus } from "./ByUrlTab";
import { useSingleSelectWrappers } from "./useSingleSelectWrappers";
import { useComposeWriteMessage } from "./useComposeWriteMessage";
import { useBulkAddToList } from "./useBulkAddToList";
import { useAddToContacts } from "./useAddToContacts";
import { useConfirmSelection } from "./useConfirmSelection";
import { GlobalSearchFooter } from "./GlobalSearchFooter";
import { GlobalSearchTabsBar } from "./GlobalSearchTabsBar";
import { ProgressBar } from "./ProgressBar";
import { ReferralSearchContext, SingleAddRequest } from "./referralContext";
import { Tabs } from "./tabs";

interface GlobalSearchModalProps {
	isOpened: boolean;
	setIsOpened: Dispatch<SetStateAction<boolean>>;
	// When provided, the modal adds selected people directly to this list (no list
	// picker) and closes after a successful add — lets this modal be reused as an
	// "add lead to a specific list" flow. Omit for the global list-picker behavior.
	targetList?: { id: number; name: string };
	title?: string;
	// When provided, the modal opens on the "By search filters" tab with this company's
	// domain pre-applied as a filter — lets callers launch a company-scoped people search
	// (e.g. "add a person from this company"). Omit for the default "By name" entry point.
	initialFilterCompany?: { name: string; domain: string };
	// Single-select "compose" mode: at most one person can be selected across all tabs, and the
	// footer action becomes "Write message" — it adds the one person to the Compose list and
	// closes (mirrors the legacy ComposeModal). Omit for the default multi-select "Add to list".
	singleSelect?: boolean;
	// Called after a successful "Write message" add (singleSelect mode) with the new lead's id (when
	// the backend returns one). Lets the Compose page react — relax its priority filter so the
	// freshly-added, not-yet-scored lead shows, and select that exact lead by id.
	onPersonAdded?: (newLeadId?: number) => void;
	// Confirm-selection mode: instead of adding the selection to a list, every selected person is
	// resolved to a LinkedIn URL and the URLs are handed to this callback — the caller runs its own
	// mutation (e.g. replace a dummy prospect, add people to a tracker organization). The modal
	// closes on success and stays open (with a snackbar) when the callback rejects.
	onConfirmSelection?: (linkedinUrls: string[]) => void | Promise<void>;
	// Footer button label for confirm-selection mode (e.g. "Select prospect", "Add Person").
	confirmLabel?: string;
	// Wrong-person referral flow: opens on the filters tab with company/department/country/name
	// prefilled from the reply context, a footer note explaining the prefill, and per-result
	// "Add to outreach" buttons. See referralContext.ts.
	referralContext?: ReferralSearchContext;
	// Set when the modal is launched from inside an already-open ContactLeadPanel (e.g. the
	// Profiles tab's "Add Person"). The modal's own embedded lead-panel preview shares the
	// panel's redux open-flag, so without this it mounts a duplicate panel *over* the dialog
	// (isZHigher 1500 > modal 1000). Suppressing it keeps the host's panel as the only instance,
	// below the dialog.
	suppressLeadPanel?: boolean;
	/** After a successful "Add to list" (multi-select / `targetList`) — lets the host refresh. */
	onAddToListSuccess?: (addedCount: number) => void;
}

export const GlobalSearchModal: FunctionComponent<GlobalSearchModalProps> = ({
	isOpened,
	setIsOpened,
	targetList,
	title,
	initialFilterCompany,
	singleSelect = false,
	onPersonAdded,
	onConfirmSelection,
	confirmLabel,
	referralContext,
	suppressLeadPanel = false,
	onAddToListSuccess,
}) => {
	const { t } = useTranslation();
	const trackFeature = useTrackFeature();
	const wasOpenedRef = useRef(false);

	useEffect(() => {
		if (isOpened && !wasOpenedRef.current) {
			trackFeature("global-search", "view");
		}

		wasOpenedRef.current = isOpened;
	}, [isOpened, trackFeature]);
	const dispatch = useAppDispatch();

	useCampaignStatusPolling();

	// Opening with a prefilled company or a referral context jumps straight to the filters
	// tab so the caller's prefill is the starting point rather than the name search.
	const [currentTab, setCurrentTab] = useState<Tabs>(
		initialFilterCompany || referralContext ? Tabs.BY_FILTERS : Tabs.BY_NAME
	);

	// ── Selection state (shared across tabs) ─────────────────────────────────
	const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
	const [selectedLinkedInItems, setSelectedLinkedInItems] = useState<
		{
			linkedinUrl: string;
			name: string;
			domain?: string;
			companyEnrichId?: number;
			// Resolved from a pasted LinkedIn URL (ByUrlTab) — added by prospectId synchronously.
			prospectId?: number;
			image?: string;
		}[]
	>([]);
	const [prefilledFilterCompany, setPrefilledFilterCompany] = useState<
		{ name: string; domain: string } | undefined
	>(initialFilterCompany);

	// ── Enrich toggles ────────────────────────────────────────────────────────
	const [isEnrichAllEmails, setIsEnrichAllEmails] = useState(false);
	const [isEnrichAllPhones, setIsEnrichAllPhones] = useState(false);

	// Footer state reported by the By URL tab: invalid chips block "Add to list", and the counts
	// drive the footer summary line.
	const [urlFooterStatus, setUrlFooterStatus] = useState<ByUrlFooterStatus>({
		hasInvalidChips: false,
		resolvedCount: 0,
		nameResultsCount: 0,
	});

	// Bumped after a successful add so the By URL tab can reset its pasted chips — an explicit
	// signal rather than inferring "added" from the selection going empty (which deselecting
	// the last card would also do).
	const [addedSignal, setAddedSignal] = useState(0);

	// ── Popover ───────────────────────────────────────────────────────────────
	const [openPopover, setOpenPopover] = useState<"addTo" | null>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const togglePopover = (type: "addTo") => setOpenPopover((prev) => (prev === type ? null : type));

	// ── Queries ───────────────────────────────────────────────────────────────
	const lastDetailQueryPromise = useRef<ReturnType<typeof triggerGetContactProspectDetail> | null>(
		null
	);
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// ── Search results (single source of truth — both tabs feed the slice) ───
	const { contacts, companyEnrich, organizations, selectedCompany } =
		useAppSelector(searchSelector);

	// ── Lead Panel "Chat" tab ────────────────────────────────────────────────
	// Clicking a contact result opens the shared ContactLeadPanel and lazy-loads its detail
	// (handleContactNavigate). Resolving the most-recent list membership to a ListPersonId turns
	// on the panel's per-lead Chat tab (draft / generate / send), mirroring the Chrome extension.
	// Gated on `isOpened` so the always-mounted GlobalSearchModal call-sites never inject the Chat
	// tab into a lead panel opened from a different surface (the panel is a shared slice overlay).
	const { leadPanelDetail } = useAppSelector(contactSelector);
	const chatListPersonId = useMemo(
		() => (isOpened ? resolveChatListPersonId(leadPanelDetail) : undefined),
		[isOpened, leadPanelDetail]
	);

	// ── Compose "Write message" (single-select) flow — see useComposeWriteMessage ──
	const { isWriting, progress, currentFunnyText, handleWriteMessage } = useComposeWriteMessage({
		selectedContactIds,
		setSelectedContactIds,
		selectedLinkedInItems,
		setSelectedLinkedInItems,
		searchContacts: contacts.data,
		isEnrichAllEmails,
		isEnrichAllPhones,
		onPersonAdded,
		setIsOpened,
	});

	// ── Multi-select "Add to list" flow — see useBulkAddToList ───────────────
	const { isBulkAdding, handleAddToList } = useBulkAddToList({
		selectedContactIds,
		setSelectedContactIds,
		selectedLinkedInItems,
		setSelectedLinkedInItems,
		isEnrichAllEmails,
		isEnrichAllPhones,
		targetList,
		setIsOpened,
		handleOpenSnackbar,
		onAddSuccess: () => setAddedSignal((n) => n + 1),
		onAddSettled: () => setOpenPopover(null),
		onAddToListSuccess,
	});

	// ── "Add to contact" flow — see useAddToContacts ─────────────────────────
	const { isAddingToContacts, handleAddToContacts } = useAddToContacts({
		selectedContactIds,
		setSelectedContactIds,
		selectedLinkedInItems,
		setSelectedLinkedInItems,
		setIsOpened,
		handleOpenSnackbar,
		onAddSuccess: () => setAddedSignal((n) => n + 1),
		onAddSettled: () => setOpenPopover(null),
	});

	// ── Confirm-selection mode — see useConfirmSelection ─────────────────────
	const { isConfirming, handleConfirmSelection } = useConfirmSelection({
		selectedContactIds,
		setSelectedContactIds,
		selectedLinkedInItems,
		setSelectedLinkedInItems,
		searchContacts: contacts.data,
		onConfirmSelection,
		setIsOpened,
		handleOpenSnackbar,
	});

	// ── Derived ───────────────────────────────────────────────────────────────
	const totalSelected = selectedContactIds.length + selectedLinkedInItems.length;

	const foundEntities = useMemo(() => {
		const displayableLinkedIn = companyEnrich.items.filter(hasDisplayableCompany).length;
		const totalFound = contacts.data.length + companyEnrich.items.length;
		const contactsItems = contacts.data.length;
		const linkedinItems = companyEnrich.items.length;
		const organizationsFound = organizations?.data.length ?? 0;
		const totalFoundForFilters = contacts.data.length + displayableLinkedIn;

		return { totalFound, contactsItems, linkedinItems, organizationsFound, totalFoundForFilters };
	}, [contacts.data, companyEnrich.items, organizations?.data]);

	const estimatedCredits = useMemo(() => {
		const email = isEnrichAllEmails ? totalSelected : 0;
		const phone = isEnrichAllPhones ? totalSelected * 10 : 0;
		// Every LinkedIn-sourced selection costs one credit: companyEnrich people (By name/
		// filters) and prospects resolved from pasted URLs alike. Tabs don't mix selections,
		// so this is the resolved count on the By URL tab and the companyEnrich count elsewhere.
		const linkedin = selectedLinkedInItems.length;

		return { total: email + phone + linkedin, email, phone, linkedin };
	}, [isEnrichAllEmails, isEnrichAllPhones, totalSelected, selectedLinkedInItems]);

	// ── Single-select wrappers (compose mode) — see useSingleSelectWrappers ───
	const { selectContactSingle, selectLinkedInSingle } = useSingleSelectWrappers({
		selectedContactIds,
		setSelectedContactIds,
		selectedLinkedInItems,
		setSelectedLinkedInItems,
	});

	// ── Effects ───────────────────────────────────────────────────────────────
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (popoverRef.current && !composedPathContains(popoverRef.current, e)) {
				setOpenPopover(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// The modal is mounted in the persistent Sidebar, so navigating away (e.g. via the lead
	// panel's "Go to inbox/outbox/list" button) would otherwise leave it open. Close it on any
	// route change while it's open.
	const location = useLocation();
	// Track the full URL (pathname + query) so a same-page navigation that only changes the
	// query — e.g. "Go to inbox" while already on the inbox — still closes the modal. The modal
	// manages its own search via Redux, not the URL, so this won't fire while the user types.
	const currentUrl = `${location.pathname}${location.search}`;
	const lastUrlRef = useRef(currentUrl);

	useEffect(() => {
		if (currentUrl === lastUrlRef.current) {
			return;
		}

		lastUrlRef.current = currentUrl;

		if (isOpened) {
			setSelectedContactIds([]);
			setSelectedLinkedInItems([]);
			dispatch(clearGlobalSearch());
			setIsOpened(false);
		}
	}, [currentUrl, isOpened, dispatch, setIsOpened]);

	// ── Handlers ──────────────────────────────────────────────────────────────
	const handleTabChange = (tab: Tabs) => {
		setCurrentTab(tab);
		setSelectedContactIds([]);
		setSelectedLinkedInItems([]);
		dispatch(clearGlobalSearch());
		if (tab !== Tabs.BY_FILTERS) {
			setPrefilledFilterCompany(undefined);
		}
	};

	// Per-row "Add to outreach" (wrong-person flow): collapse the selection to exactly that
	// row and open the footer's list picker — the add itself runs through handleAddToList.
	const handleAddSingleRequest = (request: SingleAddRequest) => {
		if ("contactId" in request) {
			setSelectedLinkedInItems([]);
			setSelectedContactIds([request.contactId]);
		} else {
			setSelectedContactIds([]);
			setSelectedLinkedInItems([request.linkedInItem]);
		}

		setOpenPopover("addTo");
	};

	const handleContactNavigate = (item: ListPersonGlobalSearchInfoDTO) => {
		const preview = toContactProspectPreview(item);

		if (!preview) {
			return;
		}

		dispatch(openContactLeadPanel(preview));
		lastDetailQueryPromise.current?.abort();
		lastDetailQueryPromise.current = triggerGetContactProspectDetail(preview.id);
	};

	const handleClose = (_event?: unknown, reason?: "escapeKeyDown" | "backdropClick") => {
		if (reason === "escapeKeyDown") {
			if (selectedCompany) {
				dispatch(clearSelectedCompany());
			}

			return;
		}

		setSelectedContactIds([]);
		setSelectedLinkedInItems([]);
		dispatch(clearGlobalSearch());
		setIsOpened(false);
	};

	// ── Footer ────────────────────────────────────────────────────────────────
	const footerLeft = (
		<GlobalSearchFooter
			currentTab={currentTab}
			totalSelected={totalSelected}
			selectedCompany={selectedCompany}
			foundEntities={foundEntities}
			isEnrichAllEmails={isEnrichAllEmails}
			setIsEnrichAllEmails={setIsEnrichAllEmails}
			isEnrichAllPhones={isEnrichAllPhones}
			setIsEnrichAllPhones={setIsEnrichAllPhones}
			estimatedCredits={estimatedCredits}
			onClearSelection={() => {
				setSelectedContactIds([]);
				setSelectedLinkedInItems([]);
			}}
			popoverRef={popoverRef}
			showAddToListPopover={
				!targetList && !singleSelect && !onConfirmSelection && openPopover === "addTo"
			}
			onToggleAddToListPopover={() => togglePopover("addTo")}
			onAddToList={handleAddToList}
			showAddToContacts={!targetList && !singleSelect && !onConfirmSelection}
			onAddToContacts={handleAddToContacts}
			isAddingToContacts={isAddingToContacts}
			targetList={targetList}
			singleSelect={singleSelect}
			isBulkAdding={isBulkAdding}
			isWriting={isWriting}
			onWriteMessage={handleWriteMessage}
			urlFooterStatus={urlFooterStatus}
			confirmMode={!!onConfirmSelection}
			confirmLabel={confirmLabel}
			isConfirming={isConfirming}
			onConfirmSelection={handleConfirmSelection}
			footerNote={referralContext?.footerNote}
		/>
	);

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<>
			<Modal
				handleClose={handleClose}
				isOpened={isOpened}
				size="md"
				title={title ?? "Find a person"}
				titleFlag={targetList && targetList.name ? `Add to ${targetList.name}` : "Global"}
				dialogContentSx={{ pb: 0 }}
				footer={
					<>
						{footerLeft}
						<button
							className="px-4 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors shrink-0"
							onClick={handleClose}
						>
							Close
						</button>
					</>
				}
			>
				{/* ── Tabs ── */}
				<GlobalSearchTabsBar currentTab={currentTab} onTabChange={handleTabChange} />

				{/* ── Tab content ── */}
				{currentTab === Tabs.BY_NAME && (
					<ByNameTab
						isModalOpened={isOpened}
						singleSelect={singleSelect}
						selectedContactIds={selectedContactIds}
						setSelectedContactIds={singleSelect ? selectContactSingle : setSelectedContactIds}
						selectedLinkedInItems={selectedLinkedInItems}
						setSelectedLinkedInItems={
							singleSelect ? selectLinkedInSingle : setSelectedLinkedInItems
						}
						onContactNavigate={handleContactNavigate}
						onNavigateToFilters={(company) => {
							setPrefilledFilterCompany(company);
							handleTabChange(Tabs.BY_FILTERS);
						}}
					/>
				)}

				{currentTab === Tabs.BY_FILTERS && (
					<ByFiltersTab
						singleSelect={singleSelect}
						selectedContactIds={selectedContactIds}
						setSelectedContactIds={singleSelect ? selectContactSingle : setSelectedContactIds}
						onContactNavigate={handleContactNavigate}
						initialCompany={prefilledFilterCompany}
						referralContext={referralContext}
						onAddSingleToList={referralContext ? handleAddSingleRequest : undefined}
						selectedLinkedInItems={selectedLinkedInItems}
						setSelectedLinkedInItems={
							singleSelect ? selectLinkedInSingle : setSelectedLinkedInItems
						}
						selectedIds={selectedLinkedInItems
							.map((i) => i.companyEnrichId)
							.filter((id): id is number => id !== undefined)}
					/>
				)}

				{currentTab === Tabs.BY_URL && (
					<ByUrlTab
						singleSelect={singleSelect}
						selectedLinkedInItems={selectedLinkedInItems}
						setSelectedLinkedInItems={
							singleSelect ? selectLinkedInSingle : setSelectedLinkedInItems
						}
						selectedContactIds={selectedContactIds}
						setSelectedContactIds={singleSelect ? selectContactSingle : setSelectedContactIds}
						resetSignal={addedSignal}
						selectedIds={selectedLinkedInItems
							.map((i) => i.companyEnrichId)
							.filter((id): id is number => id !== undefined)}
						onFooterStatusChange={setUrlFooterStatus}
					/>
				)}
			</Modal>

			{/* Informative progress while the compose "Write message" add resolves + enriches. Stays
			    up through completion (100%) and a short hold, then the modal closes + navigates. */}
			{isWriting && (
				<Modal isOpened size="sm" title="">
					<div className="flex justify-center items-center flex-col p-2 gap-2">
						<div className="text-sm font-bold">Adding contact {progress}%</div>
						<ProgressBar progress={progress} />
						<div className="text-sm font-bold">{currentFunnyText}</div>
					</div>
				</Modal>
			)}

			{!suppressLeadPanel && (
				<ContactLeadPanel isZHigher chatListPersonId={chatListPersonId} chatPending={isOpened} />
			)}
			{renderMessageSnackbar()}
		</>
	);
};
